import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {PermissionService} from './permission.service';
import {SystemPermissions} from "./system-permissions";

/**
 * Route guard that blocks navigation when the current user lacks required permissions.
 *
 * How it works:
 * - Guards call {@link checkPermissions} to ensure the permission cache is loaded and to verify access.
 * - Permissions are fetched lazily via {@link PermissionService.getUserPermissions}; subsequent checks reuse the cache.
 * - On denial, it triggers {@link AuthService.lockout} to handle unauthorized access (e.g., redirect or logout flow).
 *
 * When to use:
 * - Attach to routes needing permission-based access control via `canActivate` or `canActivateChild`.
 * - Supply required permissions through `route.data['requiredPermissions']` as an array of {@link SystemPermissions}.
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionGuard {
    private readonly permissionService = inject(PermissionService);
    private readonly router = inject(Router);

    /**
     * Guards route activation.
     * - Loads permissions if not yet loaded, then verifies all `route.data['requiredPermissions']`.
     * - If access is denied, the user is locked out and navigation is canceled.
     *
     * @param {ActivatedRouteSnapshot} route Current route snapshot containing `data['requiredPermissions']`.
     * @param {RouterStateSnapshot} state Router state (not used in logic but provided by Angular).
     * @returns {Promise<boolean>} Resolves `true` when access is allowed, `false` when denied and lockout is triggered.
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.checkPermissions((route.data['requiredPermissions'] as SystemPermissions[]) || []);
    }

    /**
     * Guards child route activation.
     * - Delegates to {@link canActivate} for identical permission semantics and loading behavior.
     *
     * @param {ActivatedRouteSnapshot} route Child route snapshot.
     * @param {RouterStateSnapshot} state Router state snapshot.
     * @returns {Promise<boolean>} Resolves according to {@link canActivate}.
     */
    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.canActivate(route, state);
    }

    /**
     * Ensures permissions are loaded and checks whether all required permissions are granted.
     * - First calls {@link PermissionService.getUserPermissions} to populate the cache if empty.
     * - Then uses {@link PermissionService.hasPermissions} to validate every required permission.
     * - On success: resolves `true`.
     * - On failure: invokes {@link AuthService.lockout} and resolves `false`.
     *
     * Usage:
     * - Intended only for guard flow; not exposed publicly to keep guard orchestration consistent.
     *
     * @param {SystemPermissions[]} requiredPermissions Permissions that must all be present to allow navigation.
     * @returns {Promise<boolean>} `true` when permitted; `false` after lockout when not permitted.
     * @private
     */
    private checkPermissions(requiredPermissions: SystemPermissions[]) {
        return this.permissionService.getUserPermissions().then(() => {
            const allowed = this.permissionService.hasPermissions(requiredPermissions);
            if (allowed) {
                return Promise.resolve(true);
            } else {
                this.router.navigate(['/noperm']).then();
                return Promise.resolve(false);
            }
        });
    }
}
